import { supabase } from '../lib/supabase';

const SELECT = '*, contacts(full_name, company)';

const fail = (error, fallback) => {
  throw new Error(error?.message || fallback);
};

export const opportunityWatchService = {
  list: async (userId) => {
    const { data, error } = await supabase
      .from('opportunity_watches')
      .select(SELECT)
      .eq('user_id', userId)
      .order('created_at', { ascending: false });
    if (error) fail(error, 'Could not load your watches.');
    return data || [];
  },

  create: async ({ userId, title, instructions, trigger, contactId, momentId, frequency, stopDay, timezone, consent }) => {
    const { data, error } = await supabase
      .from('opportunity_watches')
      .insert({
        user_id: userId,
        title: (title || '').trim(),
        instructions: (instructions || '').trim(),
        trigger_description: (trigger || '').trim(),
        contact_id: contactId || null,
        moment_id: momentId || null,
        frequency,
        status: 'watching',
        stop_after: stopDay || null,
        timezone: timezone || 'UTC',
        unattended_consent: !!consent,
        consent_at: consent ? new Date().toISOString() : null
      })
      .select(SELECT)
      .single();
    if (error) fail(error, 'Could not start the watch.');
    return data;
  },

  update: async (id, userId, { title, instructions, trigger, frequency, stopDay, consent }) => {
    const { data, error } = await supabase
      .from('opportunity_watches')
      .update({
        title: (title || '').trim(),
        instructions: (instructions || '').trim(),
        trigger_description: (trigger || '').trim(),
        frequency,
        stop_after: stopDay || null,
        unattended_consent: !!consent,
        consent_at: consent ? new Date().toISOString() : null
      })
      .eq('id', id)
      .eq('user_id', userId)
      .select(SELECT)
      .single();
    if (error) fail(error, 'Could not update the watch.');
    return data;
  },

  setStatus: async (id, userId, status) => {
    const { error } = await supabase
      .from('opportunity_watches')
      .update({ status })
      .eq('id', id)
      .eq('user_id', userId);
    if (error) fail(error, 'Could not change the watch.');
  },

  remove: async (id, userId) => {
    const { error } = await supabase.from('opportunity_watches').delete().eq('id', id).eq('user_id', userId);
    if (error) fail(error, 'Could not delete the watch.');
  },

  findings: async (watchId) => {
    const { data, error } = await supabase
      .from('watch_findings')
      .select('*')
      .eq('watch_id', watchId)
      .neq('state', 'dismissed')
      .order('created_at', { ascending: false })
      .limit(30);
    if (error) fail(error, 'Could not load findings.');
    return data || [];
  },

  // Once a finding is reviewed or dismissed, its notification is marked read, and a watch
  // showing "New information found" returns to "Watching" when no new findings remain.
  setFindingState: async (id, state, watchId) => {
    const { error } = await supabase.from('watch_findings').update({ state }).eq('id', id);
    if (error) fail(error, 'Could not update the finding.');
    if (state === 'new') return;
    await supabase
      .from('app_notifications')
      .update({ read_at: new Date().toISOString() })
      .eq('finding_id', id)
      .is('read_at', null);
    if (!watchId) return;
    const { count } = await supabase
      .from('watch_findings')
      .select('id', { count: 'exact', head: true })
      .eq('watch_id', watchId)
      .eq('state', 'new')
      .eq('classification', 'confirmed');
    if (count === 0) {
      await supabase.from('opportunity_watches').update({ status: 'watching' }).eq('id', watchId).eq('status', 'new_info');
    }
  },

  notifications: async (userId) => {
    const { data, error } = await supabase
      .from('app_notifications')
      .select('*')
      .eq('user_id', userId)
      .is('read_at', null)
      .order('created_at', { ascending: false })
      .limit(20);
    if (error) fail(error, 'Could not load notifications.');
    return data || [];
  },

  markRead: async (id) => {
    const { error } = await supabase
      .from('app_notifications')
      .update({ read_at: new Date().toISOString() })
      .eq('id', id);
    if (error) fail(error, 'Could not update the notification.');
  }
};